import React, { useEffect, useMemo, useState } from "react";
import AppLayout from "../components/AppLayout";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Camera, RefreshCw, Mail, Send, Phone, FileSignature, Check, X as XIcon,
  Plus, Trash2, ExternalLink, AlertCircle, Film, Search,
} from "lucide-react";

// State machine for a (video, actor) consent record
const STATUS_OPTIONS = [
  { v: "not_sent",    label: "Not sent",     hex: "#71717a" },
  { v: "form_sent",   label: "Form sent",    hex: "#f59e0b" },
  { v: "form_signed", label: "Signed",       hex: "#06B6D4" },
  { v: "submitted",   label: "Submitted",    hex: "#14B8A6" },
  { v: "published",   label: "Published",    hex: "#22c55e" },
  { v: "rejected",    label: "Rejected",     hex: "#ef4444" },
];
const STATUS_HEX = Object.fromEntries(STATUS_OPTIONS.map(s => [s.v, s.hex]));
const STATUS_LABEL = Object.fromEntries(STATUS_OPTIONS.map(s => [s.v, s.label]));
const NEXT_STATUS = {
  not_sent: "form_sent",
  form_sent: "form_signed",
  form_signed: "submitted",
  submitted: "published",
};

const FU_KINDS = [
  { v: "dm",      label: "DM",      icon: Send },
  { v: "email",   label: "Email",   icon: Mail },
  { v: "call",    label: "Call",    icon: Phone },
  { v: "meeting", label: "Meeting", icon: Camera },
  { v: "note",    label: "Note",    icon: FileSignature },
];

export default function Faphouse() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/api/faphouse/");
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!data?.videos) return [];
    return data.videos.filter(v => {
      if (search && !v.title?.toLowerCase().includes(search.toLowerCase())) return false;
      if (statusFilter) {
        const hasMatching = (v.cast || []).some(c => (c.consent?.status || "not_sent") === statusFilter);
        if (!hasMatching) return false;
      }
      return true;
    });
  }, [data, search, statusFilter]);

  const summary = data?.summary || {};
  const byStatus = summary.by_status || {};

  return (
    <AppLayout title="Faphouse Consent" overline="MODEL RELEASE WORKFLOW · CONSENT TRACKER · FOLLOW-UPS"
      actions={
        <button onClick={load} className="btn-ghost inline-flex items-center gap-1.5 text-[12px]" data-testid="fap-refresh">
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      }>
      {/* Stats strip */}
      <div className="grid grid-cols-2 md:grid-cols-7 gap-2 mb-5" data-testid="fap-stats">
        <KStat label="Videos" value={summary.videos_total || 0} hex="var(--gio-secondary)" />
        <KStat label="On Faphouse" value={summary.videos_with_faphouse_status || 0} hex="#fbbf24" />
        {STATUS_OPTIONS.map(s => (
          <KStat key={s.v} label={s.label} value={byStatus[s.v] || 0} hex={s.hex} clickable
            active={statusFilter === s.v}
            onClick={() => setStatusFilter(statusFilter === s.v ? "" : s.v)} testid={`fap-stat-${s.v}`} />
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--gio-muted-3)" }} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Filter by video title…"
            data-testid="fap-search"
            className="gio-input w-full pl-8" />
        </div>
        {statusFilter && (
          <button onClick={() => setStatusFilter("")} className="text-[11px] underline text-zinc-500 hover:text-white" data-testid="fap-clear-filter">
            Clear status filter
          </button>
        )}
      </div>

      {loading && <div className="py-12 text-center text-[13px]" style={{ color: "var(--gio-muted-2)" }}>Loading consent matrix…</div>}
      {!loading && filtered.length === 0 && (
        <div className="bg-zinc-950 border rounded-lg p-12 text-center" style={{ borderColor: "var(--gio-border)" }} data-testid="fap-empty">
          <AlertCircle size={32} className="mx-auto mb-3" style={{ color: "var(--gio-muted-3)" }} />
          <div className="font-heading text-lg" style={{ color: "var(--gio-text)" }}>
            {data?.videos?.length === 0 ? "No videos in the catalog yet" : "No matches for the current filter"}
          </div>
          <div className="text-[12px] mt-1" style={{ color: "var(--gio-muted-2)" }}>
            Add videos in <a href="/catalog" className="underline" style={{ color: "var(--gio-secondary)" }}>/catalog</a> and link cast from CRM to start tracking consents.
          </div>
        </div>
      )}

      <div className="space-y-4">
        {filtered.map(v => (
          <VideoConsentRow key={v.video_id} video={v} onChange={load} />
        ))}
      </div>
    </AppLayout>
  );
}

function KStat({ label, value, hex, clickable, active, onClick, testid }) {
  const Tag = clickable ? "button" : "div";
  return (
    <Tag onClick={onClick} type={clickable ? "button" : undefined}
      data-testid={testid}
      className={`bg-zinc-950 border rounded-lg p-2.5 text-left transition ${clickable ? "hover:-translate-y-0.5 cursor-pointer" : ""}`}
      style={{
        borderColor: active ? hex : "var(--gio-border)",
        boxShadow: active ? `0 0 0 1px ${hex}` : undefined,
      }}>
      <div className="overline" style={{ color: hex }}>{label}</div>
      <div className="font-heading text-2xl mt-0.5" style={{ color: "var(--gio-text)" }}>{value}</div>
    </Tag>
  );
}

function VideoConsentRow({ video, onChange }) {
  const cast = video.cast || [];
  const consents = cast.filter(c => c.consent);
  const allSubmitted = consents.length > 0 && consents.every(c => ["submitted", "published"].includes(c.consent.status));
  return (
    <div className="bg-zinc-950 border rounded-lg overflow-hidden"
      style={{ borderColor: allSubmitted ? "rgba(34,197,94,0.4)" : "var(--gio-border)" }}
      data-testid={`fap-video-${video.video_id}`}>
      {/* Video header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b"
        style={{ borderColor: "var(--gio-border)", background: "var(--gio-surface-hi)" }}>
        {video.mux_playback_id ? (
          <img src={`https://image.mux.com/${video.mux_playback_id}/thumbnail.jpg?width=160&height=90&fit_mode=smartcrop&time=2`}
            alt="" className="w-20 h-12 object-cover rounded" />
        ) : (
          <div className="w-20 h-12 rounded flex items-center justify-center" style={{ background: "var(--gio-surface)" }}>
            <Film size={16} style={{ color: "var(--gio-muted-3)" }} />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="font-heading text-[15px] truncate" style={{ color: "var(--gio-text)" }}>{video.title}</div>
          <div className="text-[11px] mt-0.5 inline-flex gap-2 items-center" style={{ color: "var(--gio-muted-2)" }}>
            {video.city && <span>{video.city}</span>}
            {video.upload_date && <><span>·</span><span>{video.upload_date}</span></>}
            <span>·</span>
            <span>{cast.length} cast member{cast.length !== 1 ? "s" : ""}</span>
            {video.faphouse_status && video.faphouse_status !== "none" && (
              <>
                <span>·</span>
                <span className="font-mono-ui px-1.5 py-0.5 rounded text-[10px]"
                  style={{ background: "#fbbf24", color: "#000" }}>FAP: {video.faphouse_status}</span>
              </>
            )}
          </div>
        </div>
        {allSubmitted && (
          <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded inline-flex items-center gap-1"
            style={{ background: "rgba(34,197,94,0.15)", color: "#22c55e" }}>
            <Check size={10} /> All consents in
          </span>
        )}
      </div>

      {/* Cast x consent matrix */}
      {cast.length === 0 ? (
        <div className="p-5 text-[12px] italic" style={{ color: "var(--gio-muted-3)" }}>
          No cast linked. Add cast from <a href="/catalog" className="underline" style={{ color: "var(--gio-secondary)" }}>/catalog</a>.
        </div>
      ) : (
        <div className="divide-y" style={{ borderColor: "var(--gio-border)" }}>
          {cast.map(c => (
            <CastConsentCell key={c.crm_id} videoId={video.video_id} cast={c} onChange={onChange} />
          ))}
        </div>
      )}
    </div>
  );
}

function CastConsentCell({ videoId, cast, onChange }) {
  const consent = cast.consent;
  const status = consent?.status || "not_sent";
  const [busy, setBusy] = useState(false);
  const [showFu, setShowFu] = useState(false);
  const [fuKind, setFuKind] = useState("dm");
  const [fuNote, setFuNote] = useState("");

  const setStatus = async (newStatus) => {
    setBusy(true);
    try {
      await api.post("/api/faphouse/consent", { video_id: videoId, crm_id: cast.crm_id, status: newStatus });
      toast.success(`${cast.stage_name} → ${STATUS_LABEL[newStatus]}`);
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setBusy(false); }
  };

  const setFormUrl = async (url) => {
    setBusy(true);
    try {
      await api.post("/api/faphouse/consent", {
        video_id: videoId, crm_id: cast.crm_id, form_url: url,
        status: status === "not_sent" ? "form_sent" : status,
      });
      toast.success("Form URL saved");
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const addFollowUp = async () => {
    if (!fuNote.trim() || !consent?.id) {
      // Need to create the consent first if it doesn't exist
      if (!consent?.id) {
        await api.post("/api/faphouse/consent", { video_id: videoId, crm_id: cast.crm_id });
        const r = await api.get("/api/faphouse/");
        const v = r.data.videos.find(x => x.video_id === videoId);
        const c = v?.cast.find(x => x.crm_id === cast.crm_id);
        if (c?.consent?.id) {
          await api.post(`/api/faphouse/consent/${c.consent.id}/follow-up`, { kind: fuKind, note: fuNote });
        }
      } else {
        await api.post(`/api/faphouse/consent/${consent.id}/follow-up`, { kind: fuKind, note: fuNote });
      }
      setFuNote(""); onChange();
      toast.success("Follow-up logged");
    }
  };

  const next = NEXT_STATUS[status];

  return (
    <div className="px-4 py-3" data-testid={`fap-cell-${videoId}-${cast.crm_id}`}>
      <div className="flex items-start gap-3 flex-wrap">
        {/* Actor avatar + name */}
        <div className="flex items-center gap-2 min-w-[180px]">
          {cast.photo ? (
            <img src={cast.photo} alt="" className="w-9 h-9 rounded-full object-cover" />
          ) : (
            <div className="w-9 h-9 rounded-full flex items-center justify-center font-heading text-sm"
              style={{ background: "var(--gio-primary-soft)", color: "var(--gio-primary)" }}>
              {(cast.stage_name || "?").slice(0, 2).toUpperCase()}
            </div>
          )}
          <div className="min-w-0">
            <div className="font-bold text-[13px] truncate" style={{ color: "var(--gio-text)" }}>{cast.stage_name}</div>
            <div className="text-[10px] truncate" style={{ color: "var(--gio-muted-3)" }}>
              {cast.email || cast.twitter || cast.phone || "no contact"}
            </div>
          </div>
        </div>

        {/* Status pill + transition button */}
        <div className="flex items-center gap-2 flex-wrap">
          <select value={status} onChange={(e) => setStatus(e.target.value)} disabled={busy}
            data-testid={`fap-status-${videoId}-${cast.crm_id}`}
            className="text-[11px] font-bold uppercase tracking-wider px-2 py-1 rounded border bg-transparent outline-none"
            style={{ borderColor: STATUS_HEX[status], color: STATUS_HEX[status] }}>
            {STATUS_OPTIONS.map(s => <option key={s.v} value={s.v} style={{ background: "#0d0d10" }}>{s.label}</option>)}
          </select>
          {next && (
            <button onClick={() => setStatus(next)} type="button" disabled={busy}
              data-testid={`fap-advance-${videoId}-${cast.crm_id}`}
              className="text-[10px] inline-flex items-center gap-1 px-2 py-1 rounded border transition hover:border-[color:var(--gio-secondary)]"
              style={{ borderColor: "var(--gio-border)", color: "var(--gio-muted)" }}
              title={`Mark as ${STATUS_LABEL[next]}`}>
              → {STATUS_LABEL[next]}
            </button>
          )}
        </div>

        {/* Form URL + signed URL */}
        <div className="flex items-center gap-2 flex-wrap text-[11px]">
          {consent?.form_url ? (
            <a href={consent.form_url} target="_blank" rel="noreferrer"
              data-testid={`fap-form-link-${videoId}-${cast.crm_id}`}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded hover:underline"
              style={{ background: "rgba(245,158,11,0.15)", color: "#f59e0b" }}>
              <FileSignature size={10} /> form <ExternalLink size={9} />
            </a>
          ) : (
            <button onClick={() => {
              const url = window.prompt(`Form URL for ${cast.stage_name}? (Cloudinary, Google Forms, DocuSign…)`);
              if (url) setFormUrl(url);
            }} type="button"
              data-testid={`fap-form-add-${videoId}-${cast.crm_id}`}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded border-dashed border transition hover:border-[color:var(--gio-secondary)]"
              style={{ borderColor: "var(--gio-border)", color: "var(--gio-muted-3)" }}>
              <Plus size={10} /> add form
            </button>
          )}
          {consent?.signed_url && (
            <a href={consent.signed_url} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded hover:underline"
              style={{ background: "rgba(34,197,94,0.15)", color: "#22c55e" }}>
              <Check size={10} /> signed <ExternalLink size={9} />
            </a>
          )}
        </div>

        {/* Follow-ups summary */}
        <button onClick={() => setShowFu(!showFu)} type="button"
          data-testid={`fap-fu-toggle-${videoId}-${cast.crm_id}`}
          className="ml-auto inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded transition"
          style={{ color: "var(--gio-secondary)" }}>
          <Send size={11} /> {(consent?.follow_ups || []).length} follow-ups
        </button>
      </div>

      {/* Follow-ups expanded */}
      {showFu && (
        <div className="mt-3 ml-12 pl-4 border-l-2 space-y-2" style={{ borderColor: "var(--gio-border)" }}
          data-testid={`fap-fu-list-${videoId}-${cast.crm_id}`}>
          {/* Quick log */}
          <div className="flex items-center gap-2 flex-wrap">
            {FU_KINDS.map(k => {
              const Icon = k.icon;
              return (
                <button key={k.v} onClick={() => setFuKind(k.v)} type="button"
                  className="text-[10px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded border"
                  style={{
                    borderColor: fuKind === k.v ? "var(--gio-primary)" : "var(--gio-border)",
                    color: fuKind === k.v ? "var(--gio-primary)" : "var(--gio-muted)",
                  }}>
                  <Icon size={9} /> {k.label}
                </button>
              );
            })}
            <input value={fuNote} onChange={(e) => setFuNote(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") addFollowUp(); }}
              data-testid={`fap-fu-note-${videoId}-${cast.crm_id}`}
              placeholder="Quick log: what did you say/send?"
              className="flex-1 min-w-[200px] bg-black border px-2 py-1 text-[11px] rounded outline-none"
              style={{ borderColor: "var(--gio-border)", color: "var(--gio-text)" }} />
            <button onClick={addFollowUp} type="button" disabled={!fuNote.trim()}
              data-testid={`fap-fu-add-${videoId}-${cast.crm_id}`}
              className="text-[10px] px-2 py-1 rounded font-bold uppercase disabled:opacity-40"
              style={{ background: "var(--gio-primary)", color: "#fff" }}>
              + Log
            </button>
          </div>
          {/* Timeline */}
          {(consent?.follow_ups || []).slice().reverse().map(f => {
            const k = FU_KINDS.find(x => x.v === f.kind) || FU_KINDS[0];
            const Icon = k.icon;
            return (
              <div key={f.id} className="flex items-start gap-2 text-[12px]"
                data-testid={`fap-fu-${f.id}`}
                style={{ color: "var(--gio-muted)" }}>
                <Icon size={11} className="mt-0.5 shrink-0" style={{ color: "var(--gio-secondary)" }} />
                <div className="flex-1 min-w-0">
                  <div style={{ color: "var(--gio-text)" }}>{f.note}</div>
                  <div className="text-[10px] mt-0.5" style={{ color: "var(--gio-muted-3)" }}>
                    {(f.date || "").slice(0, 16).replace("T", " ")} · {k.label}
                  </div>
                </div>
                <button onClick={async () => {
                  await api.delete(`/api/faphouse/consent/${consent.id}/follow-up/${f.id}`);
                  onChange();
                }}
                  data-testid={`fap-fu-del-${f.id}`}
                  className="text-zinc-600 hover:text-red-400">
                  <XIcon size={10} />
                </button>
              </div>
            );
          })}
          {(!consent?.follow_ups || consent.follow_ups.length === 0) && (
            <div className="text-[11px] italic" style={{ color: "var(--gio-muted-3)" }}>No follow-ups yet — log the first one above.</div>
          )}
        </div>
      )}
    </div>
  );
}
